import React, { useMemo, useEffect, useRef, useState } from 'react';
import { LiveProvider, LiveError, LivePreview } from 'react-live';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams, useParams } from 'react-router-dom';
import { reportApi } from '@/services/api/reports';
import { searchToolApi } from '@/services/api/searchTool';
import * as echarts from 'echarts';
import dayjs from 'dayjs';

// 原有的 UI 组件
import { Card as ShadcnCard, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from "@/components/ui/pagination";
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import DataTable from '@/components/ui/data-table/index';

// 原有的图标组件
import { 
  Loader2, 
  Box, 
  Package,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  BarChart3,
  Calendar,
  Users,
  Factory,
  Truck,
  Eye
} from 'lucide-react';

// Ant Design 组件
import { 
  Input, 
  Select, 
  Button, 
  DatePicker, 
  Checkbox,
  Space,
  Spin,
  Alert as AntAlert,
  Empty,
  Statistic,
  Layout,
  Row,
  Col,
  Card as AntCard,
  Table as AntTable,
  Form,
  Switch,
  Radio,
  Slider,
  Rate,
  Progress,
  Tag,
  Divider,
  Typography,
  Tooltip,
  Popover,
  Modal,
  Drawer,
  Tabs,
  Collapse,
  Timeline,
  Steps,
  Breadcrumb,
  Menu,
  Dropdown,
  Pagination as AntPagination,
  Upload,
  ConfigProvider
} from 'antd';

// Ant Design 图标
import { 
  SearchOutlined, 
  ReloadOutlined,
  BarChartOutlined,
  BoxPlotOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  DownloadOutlined,
  UploadOutlined,
  EyeOutlined,
  SettingOutlined,
  UserOutlined,
  HomeOutlined,
  MenuOutlined,
  CloseOutlined,
  CheckOutlined,
  InfoCircleOutlined,
  ExclamationCircleOutlined,
  QuestionCircleOutlined,
  WarningOutlined,
  LoadingOutlined
} from '@ant-design/icons';

interface DynamicTsxRendererProps {
  tsxCode: string;
  searchId?: string;
}

export default function DynamicTsxRenderer({ tsxCode, searchId }: DynamicTsxRendererProps) {
  // 提供给动态组件使用的作用域对象
  const scope = useMemo(() => ({
    // React
    React,
    createElement: React.createElement,
    isValidElement: React.isValidElement,
    // React hooks
    useEffect,
    useRef,
    useState,
    useMemo,
    
    // Router hooks
    useSearchParams,
    useParams,
    
    // React Query
    useQuery,
    useQueryClient,
    
    // 原有的 UI 组件
    ShadcnCard: ShadcnCard,  // shadcn/ui Card
    CardHeader,
    CardTitle,
    CardContent,
    ShadcnTable: Table,  // shadcn/ui Table
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
    Pagination,
    PaginationContent,
    PaginationItem,
    PaginationLink,
    PaginationNext,
    PaginationPrevious,
    ShadcnAlert: Alert,  // shadcn/ui Alert
    AlertDescription,
    AlertTitle,
    Skeleton,
    DataTable,

    // 原有的图标组件
    Loader2,
    Box,
    Package,
    TrendingUp,
    AlertTriangle,
    CheckCircle,
    BarChart3,
    Calendar,
    Users,
    Factory,
    Truck,
    Eye,

    // Ant Design 组件 (直接使用原名称以兼容动态代码)
    Input,
    Select,
    Button,
    DatePicker,
    Checkbox,
    Space,
    Spin,
    Alert: AntAlert,  // Ant Design Alert (主要使用)
    Empty,
    Statistic,
    Layout,
    Row,
    Col,
    Card: AntCard,  // Ant Design Card (主要使用)
    Table: AntTable, // Ant Design Table (主要使用)
    Form,
    Switch,
    Radio,
    Slider,
    Rate,
    Progress,
    Tag,
    Divider,
    Typography,
    Tooltip,
    Popover,
    Modal,
    Drawer,
    Tabs,
    Collapse,
    Timeline,
    Steps,
    Breadcrumb,
    Menu,
    Dropdown,
    AntPagination,
    Upload,
    ConfigProvider,

    // Ant Design 图标
    SearchOutlined,
    ReloadOutlined,
    BarChartOutlined,
    BoxPlotOutlined,
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    DownloadOutlined,
    UploadOutlined,
    EyeOutlined,
    SettingOutlined,
    UserOutlined,
    HomeOutlined,
    MenuOutlined,
    CloseOutlined,
    CheckOutlined,
    InfoCircleOutlined,
    ExclamationCircleOutlined,
    QuestionCircleOutlined,
    WarningOutlined,
    LoadingOutlined,
    
    // ECharts
    echarts,

    // dayjs 日期处理库
    dayjs,

    // API 服务
    reportApi,
    searchToolApi,
    
    // 当前的 searchId
    currentSearchId: searchId,
    
    // API 类型定义
    Report: null,
    SearchToolResponse: null,
    FieldItem: null,
  }), [searchId]);

  // 处理 TSX 代码，移除 tsx 标记和导入语句
  const processedCode = useMemo(() => {
    try {
      let code = tsxCode;

      // 移除开头的 "tsx" 标记
      if (code.startsWith('tsx\n')) {
        code = code.substring(4);
      }

      if (code.startsWith('jsx\n')) {
        code = code.substring(4);
      }
      
      // 更全面的import移除逻辑
      const removeImports = (code: string): string => {
        let cleanCode = code;
        
        // 移除所有类型的import语句（更全面的正则）
        const importPatterns = [
          // import React from 'react'
          /^import\s+\w+\s+from\s+['"][^'"]*['"];?\s*$/gm,
          // import { ... } from '...'
          /^import\s*{[^}]*}\s*from\s+['"][^'"]*['"];?\s*$/gm,
          // import * as ... from '...'
          /^import\s*\*\s*as\s+\w+\s+from\s+['"][^'"]*['"];?\s*$/gm,
          // import '...'
          /^import\s+['"][^'"]*['"];?\s*$/gm,
          // import ... from '...'
          /^import\s+.*?\s+from\s+['"][^'"]*['"];?\s*$/gm,
          // 多行import（更精确的匹配）
          /^import\s*{[\s\S]*?}\s*from\s+['"][^'"]*['"];?\s*$/gm,
        ];
        
        // 应用所有模式
        importPatterns.forEach(pattern => {
          cleanCode = cleanCode.replace(pattern, '');
        });
        
        return cleanCode;
      };
      
      code = removeImports(code);
      
      // 移除多余的空行
      code = code.replace(/^\s*\n/gm, '').replace(/\n\s*\n/g, '\n');
      
      // 移除 export default
      code = code.replace(/^export\s+default\s+/gm, '');
      
      // 更智能的函数识别和包装
      const trimmedCode = code.trim();
      
      // 检查是否已经是一个完整的函数
      // const isFunctionDeclaration = /^function\s+\w+\s*\([^)]*\)\s*{[\s\S]*}$/.test(trimmedCode);
      // const isArrowFunction = /^(const|let|var)\s+\w+\s*=\s*\([^)]*\)\s*=>\s*{[\s\S]*}$/.test(trimmedCode);
      // const isComponentFunction = /^function\s+[A-Z]\w*\s*\([^)]*\)\s*{[\s\S]*}$/.test(trimmedCode);
      
      // if (isFunctionDeclaration || isArrowFunction || isComponentFunction) {
      //   // 如果已经是完整的函数，直接返回
      //   code = trimmedCode;
      // } else {
      //   // 否则包装成函数
      //   code = `function DynamicComponent() {\n${trimmedCode}\n}`;
      // }
      
      console.log('最终处理后的代码:', code);
      
      return code;
    } catch (error) {
      console.error('代码处理错误:', error);
      return `function ErrorComponent() { 
        return React.createElement('div', { 
          style: { color: 'red', padding: '16px', border: '1px solid red', borderRadius: '4px' } 
        }, '代码处理失败: ' + String(error)); 
      }`;
    }
  }, [tsxCode]);

  // 添加错误状态管理
  const [renderError, setRenderError] = useState<string | null>(null);

  // 错误边界组件
  const SafePreview = () => {
    try {
      return <LivePreview />;
    } catch (error) {
      console.error('LivePreview 渲染错误:', error);
      setRenderError(String(error));
      return (
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-md">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-5 h-5" />
            <span className="font-semibold">组件渲染失败</span>
          </div>
          <p className="text-sm">请检查动态代码的格式和语法</p>
        </div>
      );
    }
  };


  return (
    <div className="w-full">
      <ShadcnCard className="border border-slate-200">
        <CardContent className="p-0">
          {renderError ? (
            <div className="bg-red-50 border border-red-200 text-red-700 p-4 m-4 rounded-md">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-5 h-5" />
                <span className="font-semibold">组件加载失败</span>
              </div>
              <p className="text-sm mb-2">动态组件在当前环境下无法正常渲染</p>
              <details className="text-xs">
                <summary className="cursor-pointer hover:text-red-800">查看错误详情</summary>
                <pre className="mt-2 p-2 bg-red-100 rounded text-red-800 overflow-auto">
                  {renderError}
                </pre>
              </details>
            </div>
          ) : (
            <LiveProvider
              code={processedCode}
              scope={scope}
              noInline={false}
            >
              {/* 错误显示 */}
              <LiveError>
                {(error: any) => {
                  if (error) {
                    setRenderError(String(error));
                    return (
                      <div className="bg-red-50 border border-red-200 text-red-700 p-4 m-4 rounded-md">
                        <div className="flex items-center gap-2 mb-2">
                          <AlertTriangle className="w-5 h-5" />
                          <span className="font-semibold">代码生成有误</span>
                        </div>
                        <p className="text-sm mb-2">请优化关键词，尝试再次生成</p>
                        <details className="text-xs">
                          <summary className="cursor-pointer hover:text-red-800">查看错误详情</summary>
                          <pre className="mt-2 p-2 bg-red-100 rounded text-red-800 overflow-auto">
                            {error.toString()}
                          </pre>
                        </details>
                      </div>
                    );
                  }
                  return null;
                }}
              </LiveError>
              
              {/* 组件预览 */}
              <div className="p-4">
                <SafePreview />
              </div>
            </LiveProvider>
          )}
        </CardContent>
      </ShadcnCard>
    </div>
  );
}